/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  DoBootstrap,
  NgModule,
} from '@angular/core';
import LogRocket from 'logrocket';
import { FormsModule } from '@angular/forms';
import { UpgradeModule } from '@angular/upgrade/static';
import { BrowserModule, DomSanitizer, Meta } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { UIRouterUpgradeModule } from '@uirouter/angular-hybrid';
import { MatIconRegistry } from '@angular/material/icon';
import { AutocompleteModule } from '@ng-falcon/controls/autocomplete/autocomplete.module';
import { DELIGHT_SUPPORT_CONFIG, DELIGHT_DISPLAY_NAME } from '@ng-falcon/delight-support/delight-support.tokens';
import { AVATAR_SERVICE } from '@ng-falcon/features/avatar/avatar.service';
import { TESTER_NAVIGATION_HEADER_SERVICE } from '@ng-falcon/features/tester-navigation-header/tester-navigation-header.service';
import { TESTER_NAVIGATION_SIDEBAR_SERVICE } from '@ng-falcon/features/tester-navigation-sidebar/tester-navigation-sidebar.service';
import { NgfRichTextModule } from '@ng-falcon/ngf-rich-text/ngf-rich-text.module';
import { TextEditorTemplatesModule } from '@ng-falcon/+legacy/text-editor/text-editor-templates/text-editor-templates.module';
import { UtAvatarService } from './services/ut-avatar.service';
import { UtestNavigationHeaderService } from './services/utest-navigation-header.service';
import { UtestNavigationSidebarService } from './services/utest-navigation-sidebar.service';
import { CsrfInterceptor } from './csrf.interceptor';
import { AppComponent } from './app.component';
import { HomeModule } from './home/home.module';
import './downgraded-components';
import { UtEditorModule } from './common/ut-editor/ut-editor.module';
import { CommunityModule } from './common/community/community.module';
import { ArticleModule } from './article/article.module';
import { SharedModule } from './shared/shared.module';
import { UnifiedRegistrationModule } from './unified-registration/unified-registration.module';
import { LeaderboardModule } from './leaderboard/leaderboard.module';
import { HallOfFameModule } from './hall-of-fame/hall-of-fame.module';
import { GamificationModule } from './gamification/gamification.module';
import { EmojiModule } from './emoji/emoji.module';
import { AcademyModule } from './academy/academy.module';
import { CommunityPageModule } from './community-page/community-page.module';
import { CommunityProfileModule } from './community-profile/community-profile.module';
import { DashboardModule } from './home/dashboard/dashboard.module';
import { TopicFormPageModule } from './topic-form-page/topic-form-page.module';
import { ForumsPageModule } from './forums-page/forums-page.module';
import { ArticlesPageModule } from './articles-page/articles-page.module';
import { SearchPageModule } from './search-page/search-page.module';
import { ProjectsPageModule } from './projects-page/projects-page.module';
import { logRocketRequestSanitizer, logRocketResponseSanitizer } from '../../logrocket-sanitizers';
import { LOGROCKET_ROOT_HOSTNAME } from '../components/cookie-agreement/logrocket-cookies';

const V2_NAV_ICONS = [
  'home', 'tester-profile', 'projects', 'academy', 'community', 'moderator',
  'align-right-01', 'align-left-01',
] as const;

// The build-hash meta tag is only injected by index-transform.mjs, which only runs as part of
// the production build -- local dev (`ng serve`) has no such build, so falls back to 'local'.
function getBuildHash(): string {
  return document.querySelector('meta[name="build-hash"]')?.getAttribute('content') ?? 'local';
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [BrowserModule,
    BrowserAnimationsModule,
    UpgradeModule,
    FormsModule,
    HomeModule,
    UtEditorModule,
    UIRouterUpgradeModule.forRoot(),
    AutocompleteModule,
    CommunityModule,
    ArticleModule,
    SharedModule,
    UnifiedRegistrationModule,
    LeaderboardModule,
    HallOfFameModule,
    GamificationModule,
    EmojiModule,
    AcademyModule,
    CommunityPageModule,
    CommunityProfileModule,
    DashboardModule,
    TopicFormPageModule,
    ForumsPageModule,
    ArticlesPageModule,
    SearchPageModule,
    ProjectsPageModule,
    NgfRichTextModule,
    TextEditorTemplatesModule],
  providers: [
    Meta,
    { provide: HTTP_INTERCEPTORS, useClass: CsrfInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: AVATAR_SERVICE, useExisting: UtAvatarService },
    { provide: TESTER_NAVIGATION_HEADER_SERVICE, useExisting: UtestNavigationHeaderService },
    { provide: TESTER_NAVIGATION_SIDEBAR_SERVICE, useExisting: UtestNavigationSidebarService },
    {
      // The config object is created eagerly but populated asynchronously once
      // ConfigService.config() resolves. By the time the user clicks "Chat with
      // Support" and launchChat() reads these values, the promise will have settled.
      // tokenEndpoint calls the tester-app BFF using the profile_url base URL from config.
      provide: DELIGHT_SUPPORT_CONFIG,
      useFactory: (configService: any) => {
        const config: any = {
          appId: '',
          agentId: '',
          tokenEndpoint: '',
        };
        configService.config().then((res: any) => {
          config.appId = res.data.delight_app_id ?? '';
          config.agentId = res.data.delight_agent_id ?? '';
          config.tokenEndpoint = `${res.data.profile_url}/api/auth/issue-support-token`;
        });
        return config;
      },
      deps: ['ConfigService'],
    },
    {
      // nav-header-help only mounts inside ng-if="$ctrl.currentUser", so UserService.me()
      // will have already resolved and cached its result by the time this factory runs.
      // The returned function is called lazily by DelightSupportService when launchChat()
      // is invoked, at which point displayName will always be populated.
      provide: DELIGHT_DISPLAY_NAME,
      useFactory: (userService: any) => {
        let displayName = '';
        userService.me().then((res: any) => {
          if (res?.data) {
            displayName = `${res.data.first_name ?? ''} ${res.data.last_name ?? ''}`.trim();
          }
        });
        return () => displayName;
      },
      deps: ['UserService'],
    },
  ],
})
class AppModule implements DoBootstrap {
  constructor(
    private upgrade: UpgradeModule,
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
  ) {
    V2_NAV_ICONS.forEach((icon) => {
      matIconRegistry.addSvgIcon(
        `v2-nav-${icon}`,
        domSanitizer.bypassSecurityTrustResourceUrl(`/utest-static/assets/images/v2-nav/${icon}.svg`),
      );
    });
  }

  ngDoBootstrap() {
    this.upgrade.bootstrap(document.body, ['uTestV2']);

    // utest-fe has no BFF, so LogRocket's config comes from uTestV2's own /api/v1/config
    // endpoint (via the existing AngularJS ConfigService) rather than a build-time or
    // web-apps-owned runtime source. This must happen after upgrade.bootstrap() above --
    // the AngularJS injector (and thus ConfigService) doesn't exist before that call, which
    // also rules out provideAppInitializer here (it runs before ngDoBootstrap).
    // LogRocket.identify() is wired up separately in main.ts, since it doesn't need to block
    // on this the way init() does.
    const configService: any = this.upgrade.$injector.get('ConfigService');
    const cookieAgreementService: any = this.upgrade.$injector.get('CookieAgreementService');
    configService.config().then((res: any) => {
      // LogRocket records behaviour via its own cookie, which is not strictly necessary for
      // the site to function, so it needs OneTrust Performance-cookie consent before it may
      // run at all (ENG-1083) -- same treatment as Google Analytics. Reading consent here is
      // safe despite hasAcceptedPerformanceCookies() depending on $rootScope.oneTrustCookieName:
      // index.ts's run block registered its continuation on this same cached config promise
      // first (upgrade.bootstrap() above runs it), so that name is already assigned by the time
      // this one fires. A per-load check is sufficient because any consent change reloads the
      // page (see CookieAgreementService.loadServices).
      const logRocketAppId = res?.data?.logrocket_app_id;
      if (res?.data?.logrocket_enabled && logRocketAppId && cookieAgreementService.hasAcceptedPerformanceCookies()) {
        try {
          LogRocket.init(logRocketAppId, {
            release: `${res.data.app_environment ?? 'local'}-${getBuildHash()}`,
            // Shared with the cookie cleanup in CookieAgreementService, which has to
            // reconstruct the cookie names LogRocket derives from this value.
            rootHostname: LOGROCKET_ROOT_HOSTNAME,
            dom: {
              inputSanitizer: 'lipsum',
              textSanitizer: true,
              imageSanitizer: true,
            },
            network: {
              requestSanitizer: logRocketRequestSanitizer,
              responseSanitizer: logRocketResponseSanitizer,
            },
          });
        } catch (e) {
          console.error('[LogRocket] Failed to initialize:', e);
        }
      }
    }).catch((e: any) => {
      console.error('[LogRocket] Failed to fetch config:', e);
    });
  }
}

export { AppModule };
